import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  api,
  ApiError,
  AvailableSlot,
  Client,
  Service,
  Calendar,
  CreateAppointmentInput,
  Appointment,
} from "../lib/api";

type DogSelection = string | "all" | null;

// Success modal data
interface SuccessModalData {
  appointment: Appointment;
  serviceName: string;
  clientName: string;
  dateTime: string;
}

export function NewAppointmentPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Core selections
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedDog, setSelectedDog] = useState<DogSelection>(null);
  const [selectedCalendar, setSelectedCalendar] = useState<Calendar | null>(
    null
  );
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [selectedSlot, setSelectedSlot] = useState<AvailableSlot | null>(null);

  // Optional fields
  const [notes, setNotes] = useState("");
  const [showNotes, setShowNotes] = useState(false);

  // UI state
  const [clientSearch, setClientSearch] = useState("");

  // Success modal state
  const [successModalData, setSuccessModalData] = useState<SuccessModalData | null>(null);

  // Fetch services
  const { data: servicesData, isLoading: servicesLoading } = useQuery({
    queryKey: ["services"],
    queryFn: () => api.services.list({ limit: 100 }),
  });

  // Fetch calendars
  const { data: calendarsData, isLoading: calendarsLoading } = useQuery({
    queryKey: ["calendars"],
    queryFn: () => api.calendars.list({ limit: 100 }),
  });

  // Fetch clients (with search)
  const { data: clientsData, isLoading: clientsLoading } = useQuery({
    queryKey: ["clients", clientSearch],
    queryFn: () => api.clients.list({ search: clientSearch, limit: 20 }),
  });

  // Fetch dogs for selected client
  const { data: dogsData, isLoading: dogsLoading } = useQuery({
    queryKey: ["dogs", selectedClient?.id],
    queryFn: () =>
      selectedClient ? api.dogs.byClient(selectedClient.id) : null,
    enabled: !!selectedClient,
  });

  // Fetch available slots when calendar, date, and service are selected
  const canFetchSlots = !!(
    selectedCalendar &&
    selectedDate &&
    selectedService
  );
  const {
    data: availableSlots,
    isLoading: slotsLoading,
    error: slotsError,
  } = useQuery({
    queryKey: [
      "availability",
      selectedCalendar?.id,
      selectedDate,
      selectedService?.duration,
    ],
    queryFn: () =>
      api.availability.getSlots(
        selectedCalendar!.id,
        selectedDate,
        selectedService!.duration
      ),
    enabled: canFetchSlots,
  });

  // Create appointment mutation
  const createAppointment = useMutation({
    mutationFn: (data: CreateAppointmentInput) => api.appointments.create(data),
    onSuccess: (appointment) => {
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
      
      // Format date/time for display
      const dateTime = formatDateTime(appointment.startTime);
      
      // Show success modal with appointment details
      setSuccessModalData({
        appointment,
        serviceName: selectedService?.name ?? "Service",
        clientName: selectedClient 
          ? `${selectedClient.firstName} ${selectedClient.lastName}`
          : "Client",
        dateTime,
      });
    },
  });

  // Active services and calendars
  const activeServices = servicesData?.data.filter((s) => s.isActive) ?? [];
  const activeCalendars = calendarsData?.data.filter((c) => c.isActive) ?? [];
  const clientDogs = dogsData?.data.filter((d) => d.isActive) ?? [];

  // Auto-select calendar if only one active
  useEffect(() => {
    if (activeCalendars.length === 1 && !selectedCalendar) {
      const calendar = activeCalendars[0];
      if (calendar) {
        setSelectedCalendar(calendar);
      }
    }
  }, [activeCalendars, selectedCalendar]);

  // Auto-select dog if client has exactly 1
  useEffect(() => {
    if (selectedClient && clientDogs.length === 1) {
      const dog = clientDogs[0];
      if (dog) {
        setSelectedDog(dog.id);
      }
    } else if (!selectedClient) {
      setSelectedDog(null);
    }
  }, [selectedClient, clientDogs]);

  // Check if selected date is in the past
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const selectedDateObj = selectedDate
    ? new Date(selectedDate + "T00:00:00")
    : null;
  const isPastDate = selectedDateObj ? selectedDateObj < today : false;

  // Clear selected slot when date/calendar/service changes
  useEffect(() => {
    setSelectedSlot(null);
  }, [selectedDate, selectedCalendar?.id, selectedService?.id]);

  // Check if form is valid (dog is optional - staff may not have dog info at booking time)
  const isValid =
    selectedService &&
    selectedClient &&
    selectedCalendar &&
    selectedDate &&
    selectedSlot;

  const handleSubmit = () => {
    if (
      !isValid ||
      !selectedService ||
      !selectedClient ||
      !selectedCalendar ||
      !selectedSlot
    ) {
      return;
    }

    const appointmentData: CreateAppointmentInput = {
      calendarId: selectedCalendar.id,
      clientId: selectedClient.id,
      appointmentTypeId: selectedService.id,
      startTime: selectedSlot.startTime,
      endTime: selectedSlot.endTime,
      price: selectedService.price,
      notes: notes || undefined,
    };

    // Add dog if selected (and not "all")
    if (selectedDog && selectedDog !== "all") {
      appointmentData.dogId = selectedDog;
    }

    createAppointment.mutate(appointmentData);
  };

  const isLoading = servicesLoading || calendarsLoading;

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Success Modal */}
      {successModalData && (
        <SuccessModal
          data={successModalData}
          onViewAppointment={() => navigate(`/appointments/${successModalData.appointment.id}`)}
          onBackToCalendar={() => navigate("/appointments")}
        />
      )}

      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link
          to="/appointments"
          className="text-gray-500 hover:text-gray-700"
          aria-label="Back to appointments"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">New Appointment</h1>
      </div>

      {/* Error display */}
      {createAppointment.error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">
            {createAppointment.error instanceof ApiError
              ? createAppointment.error.message
              : "Failed to create appointment"}
          </p>
        </div>
      )}

      <div className="max-w-2xl space-y-6">
        {/* Service Selection */}
        <Section title="Service" required>
          <div className="grid grid-cols-1 gap-2">
            {activeServices.map((service) => (
              <ServiceOption
                key={service.id}
                service={service}
                selected={selectedService?.id === service.id}
                onClick={() => setSelectedService(service)}
              />
            ))}
            {activeServices.length === 0 && (
              <p className="text-sm text-gray-500">
                No active services. Create a service first.
              </p>
            )}
          </div>
        </Section>

        {/* Client Selection */}
        <Section title="Client" required>
          {!selectedClient ? (
            <div className="space-y-2">
              <input
                type="text"
                placeholder="Search clients..."
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              />
              <div className="max-h-48 overflow-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
                {clientsLoading ? (
                  <div className="px-3 py-4 text-center text-sm text-gray-500">
                    Loading clients...
                  </div>
                ) : clientsData?.data.length === 0 ? (
                  <div className="px-3 py-4 text-center text-sm text-gray-500">
                    No clients found
                  </div>
                ) : (
                  clientsData?.data.map((client) => (
                    <button
                      key={client.id}
                      type="button"
                      onClick={() => {
                        setSelectedClient(client);
                        setClientSearch("");
                      }}
                      className="w-full px-3 py-2.5 text-left hover:bg-gray-50 transition-colors"
                    >
                      <div className="font-medium text-gray-800">
                        {client.firstName} {client.lastName}
                      </div>
                      <div className="text-xs text-gray-500">{client.email}</div>
                    </button>
                  ))
                )}
              </div>
            </div>
          ) : (
            <SelectedCard
              title={`${selectedClient.firstName} ${selectedClient.lastName}`}
              subtitle={selectedClient.email}
              onClear={() => {
                setSelectedClient(null);
                setSelectedDog(null);
              }}
            />
          )}
        </Section>

        {/* Dog Selection - shown when service is dog-related and client is selected (optional) */}
        {selectedService?.requiresDog && selectedClient && (
          <Section title="Dog (Optional)">
            {dogsLoading ? (
              <div className="text-sm text-gray-500">Loading dogs...</div>
            ) : clientDogs.length === 0 ? (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                This client has no dogs on file. Add a dog first.
              </div>
            ) : clientDogs.length === 1 && clientDogs[0] ? (
              <SelectedCard
                title={clientDogs[0].name}
                subtitle={clientDogs[0].breed ?? ""}
                locked
              />
            ) : clientDogs.length > 1 ? (
              <div className="space-y-2">
                {/* All Dogs option */}
                <DogOption
                  label="All Dogs"
                  subtitle={`${clientDogs.length} dogs`}
                  selected={selectedDog === "all"}
                  onClick={() => setSelectedDog("all")}
                />
                {/* Individual dogs */}
                {clientDogs.map((dog) => (
                  <DogOption
                    key={dog.id}
                    label={dog.name}
                    subtitle={dog.breed ?? ""}
                    selected={selectedDog === dog.id}
                    onClick={() => setSelectedDog(dog.id)}
                  />
                ))}
              </div>
            ) : null}
          </Section>
        )}

        {/* Calendar Selection - only show if multiple */}
        {activeCalendars.length > 1 && (
          <Section title="Calendar" required>
            <div className="grid grid-cols-2 gap-2">
              {activeCalendars.map((cal) => (
                <button
                  key={cal.id}
                  type="button"
                  onClick={() => setSelectedCalendar(cal)}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    selectedCalendar?.id === cal.id
                      ? "border-amber-500 bg-amber-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: cal.color }}
                    />
                    <span className="font-medium text-gray-800">{cal.name}</span>
                  </div>
                  {cal.description && (
                    <div className="text-xs text-gray-500 mt-0.5 ml-5">
                      {cal.description}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </Section>
        )}

        {/* Date & Time */}
        <Section title="Date & Time" required>
          <div className="space-y-3">
            {/* Date picker */}
            <div>
              <label
                htmlFor="appointment-date"
                className="block text-xs font-medium text-gray-600 mb-1"
              >
                Date
              </label>
              <input
                id="appointment-date"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              />
              {isPastDate && (
                <div className="mt-1.5 text-xs text-amber-600">
                  Past date - this will be added to history
                </div>
              )}
            </div>

            {/* Time slot selection */}
            {canFetchSlots && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">
                  Available Times
                </label>
                {slotsLoading ? (
                  <div className="text-sm text-gray-500" data-testid="slots-loading">
                    Loading available times...
                  </div>
                ) : slotsError ? (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    Failed to load available times. Please try again.
                  </div>
                ) : availableSlots && availableSlots.length > 0 ? (
                  <div className="grid grid-cols-3 gap-2">
                    {availableSlots.map((slot) => (
                      <SlotOption
                        key={slot.startTime}
                        slot={slot}
                        selected={selectedSlot?.startTime === slot.startTime}
                        onClick={() => setSelectedSlot(slot)}
                      />
                    ))}
                  </div>
                ) : (
                  <div
                    className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800"
                    data-testid="no-slots-message"
                  >
                    No available times for this date. Try another date.
                  </div>
                )}
              </div>
            )}

            {/* Prompts for missing prerequisites */}
            {!selectedService && (
              <div className="text-xs text-gray-500">
                Select a service to see available times
              </div>
            )}

            {selectedService && !selectedCalendar && (
              <div className="text-xs text-gray-500">
                Select a calendar to see available times
              </div>
            )}

            {selectedService && selectedCalendar && !selectedDate && (
              <div className="text-xs text-gray-500">
                Select a date to see available times
              </div>
            )}

            {/* Duration info */}
            {selectedService && (
              <div className="text-xs text-gray-500">
                Duration: {selectedService.duration} minutes
              </div>
            )}
          </div>
        </Section>

        {/* Notes - collapsible */}
        <div>
          <button
            type="button"
            onClick={() => setShowNotes(!showNotes)}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800"
          >
            <svg
              className={`w-4 h-4 transition-transform ${showNotes ? "rotate-180" : ""}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
            Add notes
          </button>
          {showNotes && (
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notes for this appointment..."
              rows={3}
              className="mt-2 w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-none"
            />
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4">
          <Link
            to="/appointments"
            className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 rounded-lg font-medium text-sm text-center hover:bg-gray-50 transition-colors"
          >
            Cancel
          </Link>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!isValid || createAppointment.isPending}
            className={`flex-1 px-4 py-3 rounded-lg font-medium text-sm transition-colors ${
              isValid && !createAppointment.isPending
                ? "bg-amber-600 text-white hover:bg-amber-700"
                : "bg-gray-100 text-gray-400 cursor-not-allowed"
            }`}
          >
            {createAppointment.isPending ? "Creating..." : "Create Appointment"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Format date/time for display in success modal
function formatDateTime(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

// Success modal component
function SuccessModal({
  data,
  onViewAppointment,
  onBackToCalendar,
}: {
  data: SuccessModalData;
  onViewAppointment: () => void;
  onBackToCalendar: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        {/* Success icon */}
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        </div>

        {/* Success message */}
        <h2 className="text-xl font-semibold text-center text-gray-900 mb-2">
          Appointment Created!
        </h2>
        <p className="text-center text-gray-600 mb-6">
          The appointment has been successfully scheduled.
        </p>

        {/* Appointment details */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6 space-y-3">
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 text-gray-400 mt-0.5 shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide">Service</div>
              <div className="text-sm font-medium text-gray-900">{data.serviceName}</div>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 text-gray-400 mt-0.5 shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide">Client</div>
              <div className="text-sm font-medium text-gray-900">{data.clientName}</div>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 text-gray-400 mt-0.5 shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide">Date & Time</div>
              <div className="text-sm font-medium text-gray-900">{data.dateTime}</div>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-col gap-3">
          <button
            onClick={onViewAppointment}
            className="w-full px-4 py-3 bg-amber-600 text-white rounded-lg font-medium text-sm hover:bg-amber-700 transition-colors"
          >
            View Appointment
          </button>
          <button
            onClick={onBackToCalendar}
            className="w-full px-4 py-3 border border-gray-200 text-gray-700 rounded-lg font-medium text-sm hover:bg-gray-50 transition-colors"
          >
            Back to Calendar
          </button>
        </div>
      </div>
    </div>
  );
}

// Section wrapper component
function Section({
  title,
  required,
  children,
}: {
  title: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h2 className="text-sm font-semibold text-gray-700 mb-2">
        {title}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </h2>
      {children}
    </div>
  );
}

// Service option card
function ServiceOption({
  service,
  selected,
  onClick,
}: {
  service: Service;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
        selected
          ? "border-amber-500 bg-amber-50"
          : "border-gray-200 hover:border-gray-300"
      }`}
    >
      <div className="flex items-center gap-3">
        <div
          className="w-3 h-3 rounded-full shrink-0"
          style={{ backgroundColor: service.color ?? "#6b7280" }}
        />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-gray-800">{service.name}</div>
          {service.description && (
            <div className="text-xs text-gray-500 truncate">
              {service.description}
            </div>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="text-sm font-semibold text-gray-800">
            {parseFloat(service.price) === 0
              ? "Free"
              : `$${parseFloat(service.price).toFixed(2)}`}
          </div>
          <div className="text-xs text-gray-500">{service.duration}min</div>
        </div>
      </div>
    </button>
  );
}

// Selected card with clear button
function SelectedCard({
  title,
  subtitle,
  onClear,
  locked,
}: {
  title: string;
  subtitle: string;
  onClear?: () => void;
  locked?: boolean;
}) {
  return (
    <div className="flex items-center gap-3 p-3 bg-amber-50 border-2 border-amber-500 rounded-lg">
      <div className="flex-1 min-w-0">
        <div className="font-medium text-gray-800">{title}</div>
        <div className="text-xs text-gray-500">{subtitle}</div>
      </div>
      {onClear && !locked && (
        <button
          type="button"
          onClick={onClear}
          className="text-xs text-amber-600 hover:text-amber-800 font-medium"
        >
          Change
        </button>
      )}
      {locked && <span className="text-xs text-gray-400">Only dog</span>}
    </div>
  );
}

// Dog selection option
function DogOption({
  label,
  subtitle,
  selected,
  onClick,
}: {
  label: string;
  subtitle: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 text-left transition-all ${
        selected
          ? "border-amber-500 bg-amber-50"
          : "border-gray-200 hover:border-gray-300"
      }`}
    >
      <div className="flex-1 min-w-0">
        <div className="font-medium text-gray-800">{label}</div>
        <div className="text-xs text-gray-500">{subtitle}</div>
      </div>
      {selected && (
        <div className="w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center">
          <svg
            className="w-3 h-3 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      )}
    </button>
  );
}

// Format time for display (e.g., "9:00 AM")
function formatSlotTime(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

// Time slot selection option
function SlotOption({
  slot,
  selected,
  onClick,
}: {
  slot: AvailableSlot;
  selected: boolean;
  onClick: () => void;
}) {
  const startFormatted = formatSlotTime(slot.startTime);

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`slot-${slot.startTime}`}
      className={`px-3 py-2 rounded-lg border-2 text-center text-sm font-medium transition-all ${
        selected
          ? "border-amber-500 bg-amber-50 text-amber-800"
          : "border-gray-200 hover:border-gray-300 text-gray-700"
      }`}
    >
      {startFormatted}
    </button>
  );
}
